// Purelia custom JS, loaded globally from layout/theme.liquid.
// Progressive enhancement only: if this never runs, every page still renders
// its full content, just without the entrance and scroll animations.
//
// Important: Horizon scrolls an inner container, not the window, so
// window.scrollY stays at 0 and plain scroll listeners never fire. Anything
// scroll driven here reads the element's own position each frame instead,
// and only runs while that element is actually on screen.
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Marks that JS is actually driving the page, so CSS can gate any
  // start-hidden-then-reveal animation behind this class. Without it, a
  // blocked or slow script would leave content permanently hidden instead
  // of just losing the animation.
  document.documentElement.classList.add('pl-js');

  /* Shared helper: run a per-frame callback only while el is on screen. */
  function trackOnScreen(elements, onFrame) {
    var visible = [];
    var raf = null;

    function frame() {
      for (var i = 0; i < visible.length; i++) onFrame(visible[i]);
      raf = visible.length ? window.requestAnimationFrame(frame) : null;
    }
    function start() { if (!raf) raf = window.requestAnimationFrame(frame); }

    if (!('IntersectionObserver' in window)) {
      visible = elements.slice();
      start();
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var idx = visible.indexOf(e.target);
        if (e.isIntersecting && idx === -1) visible.push(e.target);
        if (!e.isIntersecting && idx !== -1) { visible.splice(idx, 1); onFrame(e.target); }
      });
      if (visible.length) start();
    }, { threshold: 0 });

    elements.forEach(function (el) { onFrame(el); io.observe(el); });
  }

  function claim(selector, flag) {
    var out = [];
    var all = document.querySelectorAll(selector);
    for (var i = 0; i < all.length; i++) {
      if (all[i].dataset[flag]) continue;
      all[i].dataset[flag] = '1';
      out.push(all[i]);
    }
    return out;
  }

  /* Value props checklist: staggered reveal on scroll. ------------------ */
  function initChecklist() {
    // Shopify prefixes section ids with a template id that changes, so match
    // on the suffix rather than hardcoding the full id.
    var section = document.querySelector('[id^="shopify-section"][id$="__value_props_bar"]');
    if (!section || section.dataset.plFx) return;

    // Find the element whose children are the check rows: the shallowest
    // element with 3+ children where every child contains an svg icon.
    var container = null;
    var candidates = section.querySelectorAll('*');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.childElementCount < 3) continue;
      var all = true;
      for (var c = 0; c < el.children.length; c++) {
        if (!el.children[c].querySelector('svg')) { all = false; break; }
      }
      if (all) { container = el; break; }
    }
    if (!container) return;

    section.dataset.plFx = '1';
    for (var r = 0; r < container.children.length; r++) {
      var row = container.children[r];
      row.classList.add('pl-fx-row');
      row.style.setProperty('--pl-i', String(r));
    }
    section.classList.add('pl-fx-ready');

    function reveal() { section.classList.add('pl-fx-in'); }
    if (!('IntersectionObserver' in window)) { reveal(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { reveal(); io.disconnect(); } });
    }, { threshold: 0.25 });
    io.observe(section);
  }

  /* Overlay hero: the product rises past the headline as you scroll. ----- */
  function initHeroScroll() {
    if (REDUCED) return;
    var heroes = claim('.pl-hero', 'plScroll');
    if (!heroes.length) return;

    trackOnScreen(heroes, function (hero) {
      var r = hero.getBoundingClientRect();
      var h = r.height || 1;
      var p = -r.top / h;
      p = p < 0 ? 0 : (p > 1 ? 1 : p);
      hero.style.setProperty('--pl-p', p.toFixed(3));
      hero.classList.toggle('pl-hero--front', p > 0.16);
    });
  }

  /* Orbiting product story: the pinned product traces a circle. ---------- */
  function initOrbit() {
    var orbits = claim('.pl-orbit', 'plOrbit');
    if (!orbits.length) return;

    // Highlight whichever beat is closest to the middle of the viewport.
    orbits.forEach(function (el) {
      var beats = el.querySelectorAll('.pl-orbit__beat');
      if (!beats.length || !('IntersectionObserver' in window)) return;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { e.target.classList.toggle('is-active', e.isIntersecting); });
      }, { rootMargin: '-45% 0px -45% 0px' });
      beats.forEach(function (b) { io.observe(b); });
    });

    if (REDUCED) return; // keep the product still, copy stays fully visible

    trackOnScreen(orbits, function (el) {
      var radius = parseFloat(el.dataset.radius) || 0;
      var turns = parseFloat(el.dataset.turns);
      if (isNaN(turns)) turns = 1;
      var spin = parseFloat(el.dataset.spin) || 0;
      if (!radius && !spin) return;

      var r = el.getBoundingClientRect();
      // While the stage is pinned, the scrollable distance is the section
      // height minus one viewport.
      var span = r.height - window.innerHeight;
      if (span <= 0) span = r.height || 1;
      var p = -r.top / span;
      p = p < 0 ? 0 : (p > 1 ? 1 : p);

      var ang = p * turns * Math.PI * 2;
      // Starts at the resting position and traces a full circle from there.
      var ox = radius * Math.sin(ang);
      var oy = radius * (1 - Math.cos(ang));

      el.style.setProperty('--pl-ox', ox.toFixed(1) + 'px');
      el.style.setProperty('--pl-oy', oy.toFixed(1) + 'px');
      el.style.setProperty('--pl-spin', (p * spin).toFixed(1) + 'deg');
    });
  }

  /* Curtain reveal image: clip-path opens as the figure scrolls into the
     middle of the screen, driven every frame so it can never get stuck
     mid-transition the way a one-shot CSS transition can. ---------------- */
  function initReveal() {
    var figs = claim('[data-pl-reveal]', 'plReveal');
    if (!figs.length) return;

    if (REDUCED) {
      figs.forEach(function (f) { f.style.setProperty('--pl-r', '1'); });
      return;
    }

    trackOnScreen(figs, function (fig) {
      var r = fig.getBoundingClientRect();
      var vh = window.innerHeight || 1;
      // Starts opening once its top is 92% down the viewport (just coming
      // into view) and is fully open by the time its top reaches 45% up
      // the screen, well before a normally scrolling reader's eyes get there.
      var start = vh * 0.92;
      var end = vh * 0.45;
      var p = (start - r.top) / (start - end);
      p = p < 0 ? 0 : (p > 1 ? 1 : p);
      fig.style.setProperty('--pl-r', p.toFixed(3));
    });
  }

  function initAll() { initChecklist(); initHeroScroll(); initOrbit(); initReveal(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-run when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', initAll);
})();
